import React, { useContext, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Form } from 'react-bootstrap';
import ReactECharts from 'echarts-for-react';
import AppContext from 'context/Context';
import { buildFaultCodeCounts, COST_SECTIONS } from './costHelpers';

const BAR_COLOR = '#2980b9';
const SELECTED_BAR_COLOR = '#c0392b';
const DESCRIPTION_INLINE_MAX_LENGTH = 30;

const CATEGORY_FILTER_OPTIONS = [{ value: 'ALL', label: 'All' }, ...COST_SECTIONS.map(s => ({ value: s.category, label: s.title }))];

function getChartOptions(faultCounts, isDark, selectedCode) {
  const textColor = isDark ? '#d8e2ef' : '#495057';
  const axisLineColor = isDark ? 'rgba(255, 255, 255, 0.1)' : '#dee2e6';

  return {
    darkMode: isDark,
    title: {
      text: 'IPF Breakdown',
      left: 'center',
      textStyle: { fontSize: 14, fontWeight: 600, color: textColor }
    },
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      formatter: params => {
        const fault = faultCounts[params[0].dataIndex];
        const description = fault?.description || '';
        const header =
          description && description.length <= DESCRIPTION_INLINE_MAX_LENGTH
            ? `<strong>${params[0].name}</strong> — ${description}`
            : `<strong>${params[0].name}</strong>${description ? `<br/>${description}` : ''}`;
        return `${header}<br/>Count: ${params[0].value}`;
      }
    },
    grid: { left: 40, right: 20, top: 50, bottom: 60, containLabel: true },
    graphic: {
      elements: [
        {
          type: 'text',
          left: 24,
          top: 20,
          style: { text: 'Count of FAULT CODE', fill: textColor, fontSize: 12 }
        }
      ]
    },
    xAxis: {
      type: 'category',
      data: faultCounts.map(f => f.code),
      axisLine: { lineStyle: { color: axisLineColor } },
      axisLabel: { color: textColor, interval: 0, rotate: faultCounts.length > 6 ? 20 : 0 }
    },
    yAxis: {
      type: 'value',
      minInterval: 1,
      axisLine: { show: false },
      axisLabel: { color: textColor },
      splitLine: { lineStyle: { color: axisLineColor } }
    },
    series: [
      {
        type: 'bar',
        data: faultCounts.map(f => f.count),
        itemStyle: {
          color: params => (selectedCode && faultCounts[params.dataIndex].code === selectedCode ? SELECTED_BAR_COLOR : BAR_COLOR)
        },
        label: { show: true, position: 'top', color: textColor },
        barMaxWidth: 60,
        cursor: 'pointer'
      }
    ]
  };
}

export default function IpfBreakdownChart({ records, selectedCode, onSelectCode }) {
  const {
    config: { isDark }
  } = useContext(AppContext);
  const [categoryFilter, setCategoryFilter] = useState('ALL');

  const filteredRecords = useMemo(
    () => (categoryFilter === 'ALL' ? records : records.filter(r => r.category === categoryFilter)),
    [records, categoryFilter]
  );
  const faultCounts = useMemo(() => buildFaultCodeCounts(filteredRecords), [filteredRecords]);
  const options = useMemo(() => getChartOptions(faultCounts, isDark, selectedCode), [faultCounts, isDark, selectedCode]);

  const onEvents = useMemo(
    () => ({
      click: params => {
        if (params?.name) onSelectCode(params.name === selectedCode ? null : params.name);
      }
    }),
    [onSelectCode, selectedCode]
  );

  return (
    <div className="cost-ipf-chart-wrapper" style={{ borderRadius: 6, padding: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4, minHeight: 20 }}>
        <Form.Select
          size="sm"
          value={categoryFilter}
          onChange={e => {
            setCategoryFilter(e.target.value);
            onSelectCode(null);
          }}
          style={{ width: 220 }}
        >
          {CATEGORY_FILTER_OPTIONS.map(opt => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </Form.Select>

        {selectedCode && (
          <button
            type="button"
            className="cost-chart-clear-filter-btn"
            style={{ fontSize: 12, border: 'none', background: 'none', cursor: 'pointer', padding: '2px 6px' }}
            onClick={() => onSelectCode(null)}
          >
            Filtered by <strong>{selectedCode}</strong> — clear ×
          </button>
        )}
      </div>
      {faultCounts.length > 0 ? (
        <ReactECharts option={options} style={{ height: 380 }} onEvents={onEvents} />
      ) : (
        <div className="cost-summary-status text-center" style={{ fontSize: 13, padding: '24px 0' }}>
          No IPF breakdown data for this period.
        </div>
      )}
    </div>
  );
}

IpfBreakdownChart.propTypes = {
  records: PropTypes.array.isRequired,
  selectedCode: PropTypes.string,
  onSelectCode: PropTypes.func.isRequired
};
